import {FC, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import {LazyLoadImage} from 'react-lazy-load-image-component'
import {OrderItemModel} from '../models/OrderItemModel'
import {ERROR_ISSUES_DETECTED, ITEM_ISSUES_DETECTED, ItemStatus} from '../constants'
import {itemStatusClassMapping} from '../utils'
import ItemActionBox from './ItemActionBox'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {selectSelectedItems, selectSelectedMode, setSelectedItem} from '../redux/OrderRedux'
import {useDispatch} from 'react-redux'
import Lightbox from 'react-image-lightbox'
import 'react-image-lightbox/style.css'

interface Props {
  items: OrderItemModel[]
}

const ListGallery: FC<Props> = ({items}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const selectMode = useAppSelector(selectSelectedMode)
  const selectedItems = useAppSelector(selectSelectedItems)
  const [lightboxItem, setLightboxItem] = useState<OrderItemModel | undefined>(undefined)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  return (
    <>
      <div className='list-gallery'>
        {items.map((item) => {
          const isProcessing = [
            ItemStatus.AI_PROCESSING,
            ItemStatus.UPLOADED,
            ItemStatus.ANALYZING,
          ].includes(item.status)
          const isDone = item.status === ItemStatus.DONE
          const isFailed = item.status === ItemStatus.FAIL

          const inputSrc = item.inputImageUrl?.tiny
          const outputSrc =
            isDone || item.status === ItemStatus.PENDING
              ? item.outputImageUrl?.tiny
              : isFailed
              ? item.outputImageUrl?.tiny ?? item.inputImageUrl?.tiny
              : undefined

          const detectedIssues = item.note
            ? item.note
                .split(',')
                .map((t) => t.trim())
                .filter((t) => ITEM_ISSUES_DETECTED.includes(t))
            : []

          return (
            <div
              key={item.id}
              className={`list-gallery__row ${isProcessing ? 'list-gallery__row--processing' : ''}`}
            >
              {/* Checkbox */}
              {selectMode && (
                <div className='list-gallery__checkbox'>
                  <input
                    type='checkbox'
                    checked={selectedItems.includes(item.id)}
                    disabled={item.status !== ItemStatus.DONE}
                    onChange={() =>
                      dispatch(
                        setSelectedItem({id: item.id, isAdd: !selectedItems.includes(item.id)})
                      )
                    }
                  />
                </div>
              )}

              {/* Before image */}
              <div
                className='list-gallery__img'
                onClick={() => {
                  if (item.inputImageUrl) {
                    setLightboxIndex(0)
                    setLightboxItem(item)
                  }
                }}
              >
                {inputSrc ? (
                  <LazyLoadImage effect='blur' src={inputSrc} />
                ) : (
                  <div className='list-gallery__placeholder'>
                    <div className='spinner-border spinner-border-sm text-primary' role='status' />
                  </div>
                )}
              </div>

              {/* Arrow */}
              <div className='list-gallery__arrow'>
                <KTSVG
                  path='/media/icons/duotune/arrows/arr024.svg'
                  className='rotate-180 svg-icon-success svg-icon-2hx'
                />
              </div>

              {/* After image */}
              <div
                className='list-gallery__img'
                onClick={() => {
                  if (item.outputImageUrl) {
                    setLightboxIndex(1)
                    setLightboxItem(item)
                  }
                }}
              >
                {isProcessing || !outputSrc ? (
                  <div className='list-gallery__placeholder'>
                    {inputSrc && <img src={inputSrc} alt='' className='list-gallery__blur-bg' />}
                    <div className='list-gallery__placeholder-spinner'>
                      <div className='spinner-border spinner-border-sm text-white' role='status' />
                    </div>
                  </div>
                ) : (
                  <LazyLoadImage
                    effect='blur'
                    src={outputSrc}
                    onError={(event: any) => {
                      if (event?.target?.src)
                        event.target.src = inputSrc ?? '' /* fallback to input image if output image fails to load */
                    }}
                  />
                )}
              </div>

              {/* Info column */}
              <div className='list-gallery__info'>
                <Link
                  to={`/orders/${item.orderId}/items/${item.id}`}
                  className='text-dark fw-bold text-hover-primary fs-7'
                >
                  {item.name}
                </Link>
                <div className='d-flex align-items-center gap-2 mt-1 flex-wrap'>
                  <span
                    className={`badge ${itemStatusClassMapping(item.status)} fw-bold`}
                    style={{fontSize: '0.7rem'}}
                  >
                    {intl.formatMessage({id: 'Item.status.' + item.status})}
                  </span>
                  <span className='text-muted fs-8'>
                    {item.price} {intl.formatMessage({id: 'Item.Credit'})}
                  </span>
                  {detectedIssues.map((note, i) => {
                    const isError = ERROR_ISSUES_DETECTED.includes(note)
                    return (
                      <span
                        key={i}
                        className={`badge ${isError ? 'badge-danger' : 'badge-warning'} fw-bold`}
                        style={{fontSize: '0.6rem'}}
                      >
                        {intl.formatMessage({id: `ITEM.ISSUE.${note}`})}
                      </span>
                    )
                  })}
                </div>
              </div>

              {/* Actions */}
              <div className='list-gallery__actions'>
                <ItemActionBox item={item} />
              </div>
            </div>
          )
        })}
      </div>

      {/* Lightbox */}
      {lightboxItem && (
        <Lightbox
          mainSrc={
            (lightboxIndex === 0
              ? lightboxItem.inputImageUrl?.medium ?? lightboxItem.inputImageUrl?.tiny
              : lightboxItem.outputImageUrl?.medium ?? lightboxItem.outputImageUrl?.tiny) as string
          }
          nextSrc={
            lightboxItem.outputImageUrl
              ? lightboxItem.outputImageUrl.medium ?? lightboxItem.outputImageUrl.tiny
              : undefined
          }
          prevSrc={
            lightboxItem.inputImageUrl
              ? lightboxItem.inputImageUrl.medium ?? lightboxItem.inputImageUrl.tiny
              : undefined
          }
          onMoveNextRequest={() => setLightboxIndex(1)}
          onMovePrevRequest={() => setLightboxIndex(0)}
          onCloseRequest={() => setLightboxItem(undefined)}
        />
      )}
    </>
  )
}

export default ListGallery
